import BaseLayer from 'ol/layer/Base';
import { DecimalPipe } from '@angular/common';
import { Component, effect, inject } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSliderModule } from '@angular/material/slider';
import { scaleLinear } from 'd3-scale';
import GeoJSON from 'ol/format/GeoJSON';
import { Feature } from 'ol';
import { Geometry } from 'ol/geom';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import Fill from 'ol/style/Fill';
import Stroke from 'ol/style/Stroke';
import Style from 'ol/style/Style';
import Text from 'ol/style/Text';
import { map } from 'rxjs/operators';
import { AppService } from '../../../../services/app.service';
import { CoverageService } from '../../../../services/coverage.service';
import { MapService } from '../../../../services/map.service';
import { Coverage } from '../../../classes/coverage';
import { BaseUILayer } from '../../../classes/layers/base-ui-layer';
import { LayerHeaderComponent } from '../../layer-header/layer-header.component';

@Component({
  selector: 'tgrid-county-coverage-layer',
  imports: [
    MatExpansionModule,
    MatSlideToggleModule,
    MatSliderModule,
    FormsModule,
    LayerHeaderComponent,
    ReactiveFormsModule,
  ],
  templateUrl: './county-coverage-layer.component.html',
  standalone: true,
  styleUrl: './county-coverage-layer.component.scss',
})
export class CountyCoverageLayerComponent extends BaseUILayer<BaseLayer> {

  private coverageService = inject(CoverageService);
  private appService = inject(AppService);
  private numberPipe = new DecimalPipe('en-US');

  constructor() {
    super();
    effect(() => {
      this.appService.selectedStates();
      this.appService.selectedYears();
      if (this.checked) {
        this.getCountyData();
      }
    });
  }


  onDestroy(): void {
  }

  override toggleLayer(checked: boolean): void {
    this.checked = checked;
    if (this.checked) {
      this.getCountyData();
    } else {
      MapService.getMapRef()!.removeLayer(this.mapLayer().renderedLayer);
    }
  }

  getCountyData() {
    const states = this.appService.selectedStates();

    this.coverageService.getCountyLayer(states).pipe(
      map(res => {
        return {
          type: 'FeatureCollection',
          features: (res.features as {properties: Record<string, unknown>}[]).map((f) => {
            f.properties['popoverData'] = this.mapLayer().createPopover(f.properties);
            return f;
          }),
        };
      }),
    )
      .subscribe(fc => {
        const features = new GeoJSON().readFeatures(fc);
        const scale = scaleLinear([0, 20, 40, 60, 80, 100], ['#7b3294', '#c2a5cf', '#f7f7f7', '#a6dba0', '#008837']);

        if (this.mapLayer().renderedLayer && this.mapService.isLayerOnMap(this.mapLayer().renderedLayer)) {
          (this.mapLayer().renderedLayer as VectorLayer<VectorSource>).getSource()?.clear();
          (this.mapLayer().renderedLayer as VectorLayer<VectorSource>).getSource()?.addFeatures(features as Feature<Geometry>[]);
          return;
        }


        this.mapLayer().renderedLayer = new VectorLayer({
          source: new VectorSource({
            features: features as Feature<Geometry>[],
            attributions: this.mapLayer().buildAttribution(),
          }),
          properties: {
            name: this.mapLayer().displayName,
          },
          opacity: 0.8,
          style: (feature, resolution) => {

            const properties: Coverage = feature.getProperties() as Coverage;
            const isCovered = !(properties.coveredCustomers === 0 || properties.percentage === undefined);
            const color = (isCovered) ? scale(properties.percentage) : '#444';

            return new Style({
              stroke: new Stroke({
                color: 'black',
                width: 1,
              }),
              fill: new Fill({
                color: color,
              }),
              text: new Text({
                text: (resolution < 3000 && isCovered) ? `${this.numberPipe.transform(properties.percentage, '1.2-2')}%` : '',
                stroke: new Stroke({
                  color: 'black',
                  width: 1,
                }),
                fill: new Fill({
                  color: 'white',
                }),
              }),
            });

          },
        });

        MapService.getMapRef()!.addLayer(this.mapLayer().renderedLayer);
      });
  }

  override changeOpacity(opacity: number) {
    this.opacity = opacity;
    (this.mapLayer().renderedLayer as VectorLayer<VectorSource>).setOpacity(this.opacity / 100);
  }

  public exportDataAsCsv(event: MouseEvent): void {
    event.stopPropagation();
    this.mapLayer().downloadFeaturesAsCsv();

  }

}
